/**
 * Scroll-spy TOC sidebar for the Documentation preview panel.
 * Shows document heading structure and highlights the current section
 * as the user scrolls. Toggleable via button or Alt+I.
 *
 * Positioned absolutely within .markdown-panel, overlaying the right edge.
 * Independent of the inline [ld:toc] marker — works for any document with headings.
 */

export interface TocSidebarConfig {
  /** The scrollable container (#markdown-output) */
  scrollContainer: HTMLElement;
  /** The panel wrapper (.markdown-panel) */
  panel: HTMLElement;
}

interface HeadingEntry {
  level: number;
  text: string;
  id: string;
  element: HTMLElement;
  sidebarItem: HTMLElement;
}

export class TocSidebar {
  private scrollContainer: HTMLElement;
  private panel: HTMLElement;
  private sidebarEl: HTMLElement;
  private headings: HeadingEntry[] = [];
  private visible = false;
  private ticking = false;
  private activeItem: HTMLElement | null = null;
  private activeHeadingId: string | null = null;
  private hashUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollHandler: () => void;
  private skipNextScroll = false;

  constructor(config: TocSidebarConfig) {
    this.scrollContainer = config.scrollContainer;
    this.panel = config.panel;

    // Create sidebar element
    this.sidebarEl = document.createElement('nav');
    this.sidebarEl.className = 'toc-sidebar';
    this.sidebarEl.style.display = 'none';

    // Position panel as relative container
    this.panel.style.position = 'relative';
    this.panel.appendChild(this.sidebarEl);

    // Throttled scroll handler
    this.scrollHandler = () => {
      if (this.skipNextScroll) {
        this.skipNextScroll = false;
        return;
      }
      if (!this.ticking) {
        this.ticking = true;
        requestAnimationFrame(() => {
          this.onScroll();
          this.ticking = false;
        });
      }
    };

    this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  /**
   * Update sidebar content after preview refresh.
   * Collects headings from the rendered markdown and rebuilds the sidebar.
   */
  update(): void {
    this.headings = [];
    this.activeItem = null;
    this.sidebarEl.innerHTML = '';

    // Collect headings from rendered content
    const headingEls = this.scrollContainer.querySelectorAll('h1, h2, h3, h4, h5, h6');
    if (headingEls.length === 0) return;

    // Determine base level for indentation
    let minLevel = 6;
    headingEls.forEach(h => {
      const level = parseInt(h.tagName[1], 10);
      if (level < minLevel) minLevel = level;
    });

    // Build sidebar items
    const list = document.createElement('ul');
    list.className = 'toc-sidebar-list';
    const usedSlugs = new Map<string, number>();

    headingEls.forEach(h => {
      const heading = h as HTMLElement;
      const level = parseInt(heading.tagName[1], 10);
      const text = heading.textContent?.trim() ?? '';
      if (!text) return;
      const id = this.getOrCreateHeadingId(heading, text, usedSlugs);

      const li = document.createElement('li');
      li.className = `toc-sidebar-item toc-sidebar-level-${level - minLevel}`;

      const link = document.createElement('a');
      link.textContent = text;
      link.title = text;
      link.href = `#${id}`;

      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.skipNextScroll = true;
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Update active state immediately
        this.setActive(li);
        // Update URL hash without jumping
        history.pushState(null, '', `#${id}`);
        // Allow scroll events again after smooth scroll completes
        setTimeout(() => { this.skipNextScroll = false; }, 600);
      });

      li.appendChild(link);
      list.appendChild(li);

      this.headings.push({ level, text, id, element: heading, sidebarItem: li });
    });

    this.sidebarEl.appendChild(list);

    // Set initial active heading
    if (this.visible) {
      this.onScroll();
    }
  }

  private getOrCreateHeadingId(
    heading: HTMLElement,
    text: string,
    usedSlugs: Map<string, number>
  ): string {
    const existingId = heading.id?.trim();
    if (existingId) {
      // Reserve existing ID so generated IDs don't collide with it.
      usedSlugs.set(existingId, (usedSlugs.get(existingId) ?? 0) + 1);
      return existingId;
    }

    const baseSlug = this.slugify(text) || 'heading';
    const count = usedSlugs.get(baseSlug) ?? 0;
    usedSlugs.set(baseSlug, count + 1);
    const slug = count === 0 ? baseSlug : `${baseSlug}-${count + 1}`;
    heading.id = slug;
    return slug;
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  show(): void {
    this.visible = true;
    this.sidebarEl.style.display = '';
    this.scrollContainer.classList.add('has-toc-sidebar');
    this.onScroll();
  }

  hide(): void {
    this.visible = false;
    this.sidebarEl.style.display = 'none';
    this.scrollContainer.classList.remove('has-toc-sidebar');
  }

  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Handle scroll: find active heading, update URL hash, and update sidebar.
   * Runs on every scroll regardless of sidebar visibility so the URL hash
   * always reflects the current reading position.
   */
  private onScroll(): void {
    if (this.headings.length === 0) return;

    const active = this.findActiveHeading();
    if (!active) return;

    // Always update URL hash (debounced) regardless of sidebar visibility
    if (active.id !== this.activeHeadingId) {
      this.activeHeadingId = active.id;
      this.debouncedUpdateHash(active.id);
    }

    // Only update sidebar visual if visible
    if (this.visible) {
      this.setActive(active.sidebarItem);
    }
  }

  /**
   * Determine which heading is currently at the top of the viewport.
   *
   * Uses getBoundingClientRect() so it works regardless of DOM nesting
   * (e.g. headings wrapped in .checklist-section divs).
   */
  private findActiveHeading(): HeadingEntry | null {
    const containerTop = this.scrollContainer.getBoundingClientRect().top;
    const threshold = 40; // px below container top to consider "active"

    // Find the last heading whose top edge is at or above the threshold line
    let activeEntry: HeadingEntry | null = null;
    for (const entry of this.headings) {
      const headingTop = entry.element.getBoundingClientRect().top - containerTop;
      if (headingTop <= threshold) {
        activeEntry = entry;
      } else {
        break;
      }
    }

    // Default to first heading if nothing found (scrolled to very top)
    if (!activeEntry && this.headings.length > 0) {
      activeEntry = this.headings[0];
    }

    return activeEntry;
  }

  /** Debounced URL hash update via replaceState to avoid history pollution. */
  private debouncedUpdateHash(id: string): void {
    if (this.hashUpdateTimer) {
      clearTimeout(this.hashUpdateTimer);
    }
    this.hashUpdateTimer = setTimeout(() => {
      const currentHash = window.location.hash.slice(1);
      if (currentHash !== id) {
        history.replaceState(null, '', `#${id}`);
      }
    }, 250);
  }

  private setActive(item: HTMLElement): void {
    if (this.activeItem === item) return;

    if (this.activeItem) {
      this.activeItem.classList.remove('active');
    }

    item.classList.add('active');
    this.activeItem = item;

    // Scroll sidebar to keep active item visible
    item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /**
   * Sync checklist state with TOC sidebar items.
   * Adds/removes .checked class based on which heading slugs are checked.
   */
  syncChecklist(checkedSlugs: string[]): void {
    if (this.headings.length === 0) return;
    const checked = new Set(checkedSlugs);
    for (const entry of this.headings) {
      entry.sidebarItem.classList.toggle('checked', checked.has(entry.id));
    }
  }

  teardown(): void {
    if (this.hashUpdateTimer) clearTimeout(this.hashUpdateTimer);
    this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
    this.sidebarEl.remove();
    this.scrollContainer.classList.remove('has-toc-sidebar');
  }
}
